'use client';

import { CalendarIcon, LiveSportsIcon } from '@repo/icons';
import { Button } from '@repo/ui/button';
import { SideMenuItem } from '@repo/ui/side-menu-item';
import { cn } from '@repo/utils/cn';
import { useLocale, useTranslations } from 'next-intl';
import { ReactNode, useState } from 'react';
import { renderIcon } from '@/components/layout/sidebar-left/render-icon';
import { usePathname, useRouter } from '@/i18n/routing';
import { getSportCategoryItems } from '@/lib/utils/sport-categories';

type SportCategoriesProps = {
  sidebarOpen: boolean;
};

const SportCategories = ({ sidebarOpen }: SportCategoriesProps) => {
  const t = useTranslations();
  const router = useRouter();
  const pathname = usePathname();
  const locale = useLocale();

  const [eventType, setEventType] = useState<'live' | 'upcoming'>('live');

  const categories = getSportCategoryItems(t, eventType, locale);

  return (
    <div className="flex flex-col">
      <div className={cn('flex w-full items-center justify-between gap-2 py-1.5', !sidebarOpen && 'flex-col gap-1')}>
        <Button
          className="h-8 w-full truncate"
          variant={eventType === 'live' ? 'inverted' : 'secondary'}
          onClick={() => setEventType('live')}
        >
          {sidebarOpen ? (
            <span className="block truncate">{t('sidebar-left.live')}</span>
          ) : (
            <LiveSportsIcon className="absolute" />
          )}
        </Button>
        <Button
          className="h-8 w-full truncate"
          variant={eventType === 'upcoming' ? 'inverted' : 'secondary'}
          onClick={() => setEventType('upcoming')}
        >
          {sidebarOpen ? (
            <span className="block truncate">{t('sidebar-left.upcoming')}</span>
          ) : (
            <CalendarIcon className="absolute" />
          )}
        </Button>
      </div>
      {categories.map((item) => {
        const { name, href, Icon, matches, notMatches } = item;
        const isActive = !notMatches?.includes(pathname) && matches?.includes(pathname);
        return (
          <SideMenuItem
            key={href}
            variant="secondary"
            className="rounded-none"
            text={sidebarOpen && name}
            Icon={renderIcon(Icon)}
            isActive={isActive}
            onClick={() => router.push(href)}
            optionalLastElement={sidebarOpen && (eventType === 'live' ? <LiveSportsIcon /> : <CalendarIcon />)}
            tooltipText={name}
            showTooltip={!sidebarOpen}
          />
        );
      })}
    </div>
  );
};

export default SportCategories;
